import _ from 'lodash'
import { Spx } from '@wix/thunderbolt-becky-types'
import { atomicStylePropsConverterMap } from '../../atomics/atomicPropsToCss'
import { mathConverterMap } from '../MathFunction/mathPropsToCss'
import { StylePropertiesTypes } from '@wix/document-services-types'
import { isSystemFont } from './utils'
import { FontVariant } from '@wix/thunderbolt-becky-root'
import { genericsConverterMap } from '../../generics/genericsPropsToCss'

export type FontVariantFunction = Extract<FontVariant['alternates'], { type: 'Function' }>

/** Internal Converters */
const fontWeightConverter = (weight: StylePropertiesTypes.FontWeight): string => {
	return fontWeightConverterMap[weight.type](weight)
}

const fontSizeConverter = (size: StylePropertiesTypes.FontSize, spx?: Spx): string => {
	return fontSizeConverterMap[size.type](size, spx)
}

const fontStyleConverter = (style: StylePropertiesTypes.FontStyle): string => {
	if (style.value === 'oblique' && style.angle) {
		return `oblique ${atomicStylePropsConverterMap.Angle(style.angle)}`
	}
	return style.value
}

const fontLineHeightConverter = (styleProp: StylePropertiesTypes.LineHeight, spx?: Spx): string => {
	return lineHeightConverterMap[styleProp.type](styleProp, spx)
}

const fontVariantFunctionConverter = (styleProp: FontVariantFunction) =>
	`${styleProp.function}(${styleProp.values.value.join(', ')})`

const fontVariantConverter = (styleProp: FontVariant) => {
	const ligatures = styleProp.ligatures && atomicStylePropsConverterMap.Keyword(styleProp.ligatures)
	const alternates = styleProp.alternates && fontVariantConverterMap[styleProp.alternates.type](styleProp.alternates)
	const caps = styleProp.caps && atomicStylePropsConverterMap.Keyword(styleProp.caps)
	const numeric = styleProp.numeric && atomicStylePropsConverterMap.Keyword(styleProp.numeric)
	const eastAsian = styleProp.eastAsian && atomicStylePropsConverterMap.Keyword(styleProp.eastAsian)
	const position = styleProp.position && atomicStylePropsConverterMap.Keyword(styleProp.position)
	const emoji = styleProp.emoji && atomicStylePropsConverterMap.Keyword(styleProp.emoji)

	return {
		ligatures,
		alternates,
		caps,
		numeric,
		eastAsian,
		position,
		emoji,
		[styleProp.type]:
			'var(--{propName}-ligatures) var(--{propName}-alternates) var(--{propName}-caps) var(--{propName}-numeric) var(--{propName}-eastAsian) var(--{propName}-position) var(--{propName}-emoji)',
	}
}

const fontConverter = (styleProp: StylePropertiesTypes.Font, spx?: Spx) => {
	if (isSystemFont(styleProp)) {
		return { font: styleProp.value }
	}

	const { fontStyle, fontVariant, fontWeight, fontSize, lineHeight, fontFamily } = styleProp

	const convertedSize = fontSize && fontSizeConverter(fontSize, spx)
	const convertedStyle = fontStyle && fontStyleConverter(fontStyle)
	const convertedVariant = fontVariant && atomicStylePropsConverterMap.Keyword(fontVariant)
	const convertedWeight = fontWeight && fontWeightConverter(fontWeight)
	const convertedLineHeight = lineHeight && fontLineHeightConverter(lineHeight, spx)
	const convertedFamily = fontFamily && genericsConverterMap.List(fontFamily)

	return {
		style: convertedStyle,
		variant: convertedVariant,
		weight: convertedWeight,
		size: convertedSize,
		lineHeight: convertedLineHeight,
		family: convertedFamily,
		[styleProp.type]:
			'var(--{propName}-style) var(--{propName}-variant) var(--{propName}-weight) var(--{propName}-size)/var(--{propName}-lineHeight) var(--{propName}-family)',
	}
}

/** Internal Maps */
const lineHeightConverterMap = _.mergeWith({ ...mathConverterMap }, { ...atomicStylePropsConverterMap }) as Record<
	string,
	(styleProp: StylePropertiesTypes.LineHeight, spx?: Spx) => string
>

const fontSizeConverterMap = _.mergeWith({ ...mathConverterMap }, { ...atomicStylePropsConverterMap }) as Record<
	string,
	(size: StylePropertiesTypes.FontSize, spx?: Spx) => string
>

const fontWeightConverterMap = {
	Keyword: atomicStylePropsConverterMap.Keyword,
	Number: atomicStylePropsConverterMap.Number,
} as Record<string, (weight: StylePropertiesTypes.FontWeight) => string>

const fontVariantConverterMap = {
	Keyword: atomicStylePropsConverterMap.Keyword,
	Function: fontVariantFunctionConverter,
} as Record<string, (prop: StylePropertiesTypes.Keyword<string> | FontVariantFunction) => string>

/** External Map */
export const fontConverterMap = {
	Font: fontConverter,
	FontStyle: fontStyleConverter,
	FontVariant: fontVariantConverter,
}
